import { normalizeCardLayout, normalizeValueSchema } from './valuePresentation';

const INPUT_TYPE_BY_INDEX = [
  'Dropdown',
  'Radio',
  'Checkbox',
  'MultiCheckbox',
  'Slider',
  'TextInput',
  'NumberInput',
  'TextArea',
  'DatePicker',
  'ColorPicker',
  'ImageDisplay',
  'ValueCards',
  'Swatches',
  'ImageCarousel'
];

const INPUT_TYPE_ALIASES = {
  dropdown: 'Dropdown',
  radio: 'Radio',
  checkbox: 'Checkbox',
  multicheckbox: 'MultiCheckbox',
  multi_checkbox: 'MultiCheckbox',
  multi_checkboxinput: 'MultiCheckbox',
  'multi-checkbox': 'MultiCheckbox',
  'multi checkbox': 'MultiCheckbox',
  slider: 'Slider',
  textinput: 'TextInput',
  text_input: 'TextInput',
  'text-input': 'TextInput',
  'text input': 'TextInput',
  numberinput: 'NumberInput',
  number_input: 'NumberInput',
  'number-input': 'NumberInput',
  'number input': 'NumberInput',
  textarea: 'TextArea',
  text_area: 'TextArea',
  'text-area': 'TextArea',
  'text area': 'TextArea',
  datepicker: 'DatePicker',
  date_picker: 'DatePicker',
  'date-picker': 'DatePicker',
  'date picker': 'DatePicker',
  colorpicker: 'ColorPicker',
  color_picker: 'ColorPicker',
  'color-picker': 'ColorPicker',
  'color picker': 'ColorPicker',
  imagedisplay: 'ImageDisplay',
  image_display: 'ImageDisplay',
  'image-display': 'ImageDisplay',
  'image display': 'ImageDisplay',
  valuecards: 'ValueCards',
  value_cards: 'ValueCards',
  'value-cards': 'ValueCards',
  'value cards': 'ValueCards',
  swatches: 'Swatches',
  imagecarousel: 'ImageCarousel',
  image_carousel: 'ImageCarousel',
  'image-carousel': 'ImageCarousel',
  'image carousel': 'ImageCarousel'
};

const getFirstDefined = (...values) => values.find(value => value !== undefined && value !== null);

export const normalizeInputType = (inputType) => {
  if (typeof inputType === 'number') {
    return INPUT_TYPE_BY_INDEX[inputType] || 'Dropdown';
  }

  const normalized = String(inputType ?? 'Dropdown').trim();
  if (!normalized) {
    return 'Dropdown';
  }

  return INPUT_TYPE_ALIASES[normalized.toLowerCase()] || normalized;
};

export const normalizeFeatureValue = (value) => {
  if (value === null || typeof value !== 'object') {
    return {
      id: String(value ?? ''),
      value: String(value ?? ''),
      displayText: String(value ?? '')
    };
  }

  const normalizedValue = getFirstDefined(value.value, value.Value, value.id, value.Id, '');
  return {
    ...value,
    id: getFirstDefined(value.id, value.Id, normalizedValue),
    value: String(normalizedValue),
    displayText: String(getFirstDefined(value.displayText, value.DisplayText, normalizedValue)),
    description: getFirstDefined(value.description, value.Description, null),
    imageUrl: getFirstDefined(value.imageUrl, value.ImageUrl, null),
    attributes: getFirstDefined(value.attributes, value.Attributes, {}),
    propertyValues: getFirstDefined(value.propertyValues, value.PropertyValues, {}),
    sortOrder: Number(getFirstDefined(value.sortOrder, value.SortOrder, 0)) || 0,
    isDefault: Boolean(getFirstDefined(value.isDefault, value.IsDefault, false))
  };
};

export const normalizeFeatureForUi = (feature) => {
  const property = getFirstDefined(feature?.property, feature?.Property, {});
  const inputType = normalizeInputType(getFirstDefined(
    feature?.inputType,
    feature?.InputType,
    feature?.type,
    feature?.Type,
    property?.inputType,
    property?.InputType,
    property?.type,
    property?.Type
  ));

  const name = getFirstDefined(feature?.name, feature?.Name, '');
  const displayText = getFirstDefined(
    feature?.displayText,
    feature?.DisplayText,
    property?.displayText,
    property?.DisplayText,
    name
  );
  const description = getFirstDefined(
    feature?.description,
    feature?.Description,
    property?.description,
    property?.Description,
    null
  );
  const attributes = getFirstDefined(feature?.attributes, feature?.Attributes, property?.attributes, property?.Attributes, {});

  return {
    ...feature,
    id: getFirstDefined(feature?.id, feature?.Id),
    name,
    displayText,
    description,
    inputType,
    values: (getFirstDefined(feature?.values, feature?.Values, []) || []).map(normalizeFeatureValue),
    attributes,
    isSystem: Boolean(getFirstDefined(
      feature?.isSystem,
      feature?.IsSystem,
      property?.isSystem,
      property?.IsSystem,
      false
    )),
    isOutput: Boolean(getFirstDefined(
      feature?.isOutput,
      feature?.IsOutput,
      property?.isOutput,
      property?.IsOutput,
      false
    )),
    valueSchema: normalizeValueSchema(getFirstDefined(feature?.valueSchema, feature?.ValueSchema, property?.valueSchema, property?.ValueSchema, [])),
    cardLayout: normalizeCardLayout(getFirstDefined(feature?.cardLayout, feature?.CardLayout, property?.cardLayout, property?.CardLayout, null)),
    property: {
      ...property,
      displayText,
      description,
      inputType: inputType.toLowerCase(),
      attributes,
      isSystem: Boolean(getFirstDefined(property?.isSystem, property?.IsSystem, feature?.isSystem, feature?.IsSystem, false)),
      isOutput: Boolean(getFirstDefined(property?.isOutput, property?.IsOutput, feature?.isOutput, feature?.IsOutput, false)),
      valueSchema: normalizeValueSchema(getFirstDefined(property?.valueSchema, property?.ValueSchema, feature?.valueSchema, feature?.ValueSchema, [])),
      cardLayout: normalizeCardLayout(getFirstDefined(property?.cardLayout, property?.CardLayout, feature?.cardLayout, feature?.CardLayout, null))
    }
  };
};

export const selectionsFromDefaultValues = (features) => {
  const selections = {};
  (features || []).forEach(feature => {
    const name = getFirstDefined(feature?.name, feature?.Name);
    const values = getFirstDefined(feature?.values, feature?.Values, []) || [];
    const defaultValue = values.find(value => {
      if (value === null || typeof value !== 'object') return false;
      return Boolean(getFirstDefined(value.isDefault, value.IsDefault, false));
    });
    if (!name || !defaultValue) return;
    const valueText = String(getFirstDefined(defaultValue.value, defaultValue.Value, ''));
    if (valueText) {
      selections[name] = valueText;
    }
  });
  return selections;
};
